import { type IntranetContact } from '@gb-ticket/shared-types';
import { ArrowLeftIcon, Loader2Icon, PencilIcon, PlusIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import {
  intranetPanelClassName,
  intranetTableFrameClassName,
} from '@/products/intranet/features/intranet/intranet-panel';
import { useIntranetDepartments } from '@/products/intranet/features/intranet/use-intranet';
import {
  useCreateIntranetContact,
  useDeleteIntranetContact,
  useIntranetColleagues,
  useIntranetContacts,
  useUpdateIntranetContact,
} from '@/products/intranet/features/intranet/use-intranet-directory';
import { INTRANET_EDITORIAL_PATH } from '@/products/intranet/intranet-paths';

type Draft = {
  functionTitle: string;
  userId: string;
  departmentId: string;
  externalUrl: string;
  sortOrder: string;
};

const EMPTY: Draft = {
  functionTitle: '',
  userId: '',
  departmentId: '',
  externalUrl: '',
  sortOrder: '0',
};

export function ContactsManagePage() {
  usePageTitle('Ansprechpartner');
  const contacts = useIntranetContacts();
  const colleagues = useIntranetColleagues({});
  const departments = useIntranetDepartments(true);
  const create = useCreateIntranetContact();
  const update = useUpdateIntranetContact();
  const remove = useDeleteIntranetContact();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<IntranetContact | null>(null);
  const items = contacts.data?.items ?? [];
  const saving = create.isPending || update.isPending;

  function startEdit(contact: IntranetContact) {
    setEditingId(contact.id);
    setDraft({
      functionTitle: contact.functionTitle,
      userId: contact.user?.id ?? '',
      departmentId: contact.department?.id ?? '',
      externalUrl: contact.externalUrl ?? '',
      sortOrder: String(contact.sortOrder),
    });
  }

  function resetForm() {
    setEditingId(null);
    setDraft(EMPTY);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const body = {
      functionTitle: draft.functionTitle.trim(),
      userId: draft.userId || null,
      departmentId: draft.departmentId || null,
      externalUrl: draft.externalUrl.trim() || null,
      sortOrder: Number(draft.sortOrder) || 0,
    };
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, body });
        toast.success('Ansprechpartner gespeichert.');
      } else {
        await create.mutateAsync(body);
        toast.success('Ansprechpartner angelegt.');
      }
      resetForm();
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success('Ansprechpartner gelöscht.');
      if (editingId === pendingDelete.id) {
        resetForm();
      }
      setPendingDelete(null);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to={INTRANET_EDITORIAL_PATH}>
          <ArrowLeftIcon aria-hidden />
          Zur Redaktion
        </Link>
      </Button>
      <PlatformPageHeader
        title="Ansprechpartner"
        description="Funktion, Person, Abteilung oder externer Link."
      />

      <form className={`${intranetPanelClassName} grid gap-3 sm:grid-cols-2`} onSubmit={onSubmit}>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="contact-function">Funktion</Label>
          <Input
            id="contact-function"
            value={draft.functionTitle}
            maxLength={120}
            required
            onChange={(event) =>
              setDraft((current) => ({ ...current, functionTitle: event.target.value }))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-user">Person</Label>
          <Select
            value={draft.userId || 'none'}
            onValueChange={(value) =>
              setDraft((current) => ({ ...current, userId: value === 'none' ? '' : value }))
            }
          >
            <SelectTrigger id="contact-user" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keine Person</SelectItem>
              {(colleagues.data?.items ?? []).map((person) => (
                <SelectItem key={person.id} value={person.id}>
                  {person.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-dept">Abteilung</Label>
          <Select
            value={draft.departmentId || 'none'}
            onValueChange={(value) =>
              setDraft((current) => ({ ...current, departmentId: value === 'none' ? '' : value }))
            }
          >
            <SelectTrigger id="contact-dept" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keine Abteilung</SelectItem>
              {(departments.data?.items ?? []).map((department) => (
                <SelectItem key={department.id} value={department.id}>
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-url">Externer Link</Label>
          <Input
            id="contact-url"
            type="url"
            value={draft.externalUrl}
            placeholder="https://"
            onChange={(event) =>
              setDraft((current) => ({ ...current, externalUrl: event.target.value }))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-sort">Reihenfolge</Label>
          <Input
            id="contact-sort"
            type="number"
            min={0}
            max={10000}
            value={draft.sortOrder}
            onChange={(event) =>
              setDraft((current) => ({ ...current, sortOrder: event.target.value }))
            }
          />
        </div>
        <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
          <Button type="submit" disabled={saving}>
            {editingId ? <PencilIcon aria-hidden /> : <PlusIcon aria-hidden />}
            {editingId ? 'Speichern' : 'Anlegen'}
          </Button>
          {editingId ? (
            <Button type="button" variant="outline" onClick={resetForm}>
              Abbrechen
            </Button>
          ) : null}
        </div>
      </form>

      {contacts.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Liste wird geladen …
        </p>
      ) : (
        <div className={intranetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Funktion</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>Abteilung</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium">{contact.functionTitle}</TableCell>
                  <TableCell>{contact.user?.displayName ?? '—'}</TableCell>
                  <TableCell>{contact.department?.name ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(contact)}>
                      Bearbeiten
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPendingDelete(contact)}>
                      Löschen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        title="Ansprechpartner löschen?"
        description={pendingDelete ? `${pendingDelete.functionTitle} wird entfernt.` : ''}
        pending={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </section>
  );
}
